/**
 * FreezeCap™ — Shipping day calculator
 * Cutoff: 3 pm CST (America/Chicago), matching the TX fulfillment origin.
 *
 * Dispatch reality (verified against USPS operating hours):
 *   • Ship days are Mon–Fri only. We do NOT dispatch on Saturday or Sunday —
 *     USPS Saturday retail/collection closes early (~1–2 pm) and is unreliable,
 *     and there is no regular Sunday pickup. So a Fri-after-cutoff or any
 *     weekend order ships the following Monday.
 *   • USPS is closed on federal holidays (see HOLIDAYS). An order that would
 *     otherwise ship on a holiday rolls to the next open weekday.
 *
 * Output is uppercase to match the eyebrow line (e.g. "US PATENT … · IN STOCK"):
 *   "SHIPS TODAY"    — order will go out today (a ship day, before cutoff)
 *   "SHIPS TOMORROW" — next ship day is the next calendar day
 *   "SHIPS MONDAY"   — next ship day is further out (weekend / Fri-after-cutoff
 *                      / holiday). The weekday is named so we never imply a
 *                      closed day (e.g. "tomorrow" landing on a Sunday).
 *
 * Localization: display strings come from window.FC_SHIPPING_I18N (injected by
 * snippets/fc-shipping-i18n.liquid from the freezecap.shipping.* locale keys).
 * The English strings in getText() are fallbacks used when that object is absent.
 *
 * Usage:
 *   window.FCShipping.getText()        → e.g. "SHIPS TODAY" | "SHIPS TOMORROW" | "SHIPS MONDAY"
 *   window.FCShipping.inject('#my-el') → sets el.textContent to getText()
 */
(function () {
  var CUTOFF = 15; // 3 pm, on a ship day, in America/Chicago

  var WEEKDAYS = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];

  /* USPS federal holidays are COMPUTED from their calendar rules, not hand-listed,
     so this never needs a yearly refresh — only edit if Congress adds a brand-new
     federal holiday. Two rule shapes cover all 11:
       • Floating: the nth (or last) given weekday of a month — always a weekday.
       • Fixed-date: a set calendar day, shifted to the federal "observed" day when
         it lands on a weekend (Sat → prior Fri, Sun → following Mon), which is the
         day USPS actually closes. */
  var MON = 1, THU = 4;

  function nthWeekday(y, m, weekday, n) {        // m is 0-indexed
    var first = new Date(y, m, 1);
    var offset = (weekday - first.getDay() + 7) % 7;
    return new Date(y, m, 1 + offset + (n - 1) * 7);
  }
  function lastWeekday(y, m, weekday) {
    var last = new Date(y, m + 1, 0);            // last day of month m
    var offset = (last.getDay() - weekday + 7) % 7;
    return new Date(y, m, last.getDate() - offset);
  }
  function observed(y, m, day) {                 // fixed-date → federal observed day
    var d = new Date(y, m, day), w = d.getDay();
    if (w === 6) return new Date(y, m, day - 1); // Saturday → Friday
    if (w === 0) return new Date(y, m, day + 1); // Sunday → Monday
    return d;
  }

  function holidayKey(d) {
    return d.getFullYear() + '-' + (d.getMonth() + 1) + '-' + d.getDate();
  }

  var holidayCache = {};
  function holidaysForYear(y) {
    if (holidayCache[y]) return holidayCache[y];
    var set = {};
    function add(d) { set[holidayKey(d)] = true; }
    add(observed(y, 0, 1));        // New Year's Day — Jan 1
    add(nthWeekday(y, 0, MON, 3)); // MLK Jr. Day — 3rd Mon Jan
    add(nthWeekday(y, 1, MON, 3)); // Presidents' Day — 3rd Mon Feb
    add(lastWeekday(y, 4, MON));   // Memorial Day — last Mon May
    add(observed(y, 5, 19));       // Juneteenth — Jun 19
    add(observed(y, 6, 4));        // Independence Day — Jul 4
    add(nthWeekday(y, 8, MON, 1)); // Labor Day — 1st Mon Sep
    add(nthWeekday(y, 9, MON, 2)); // Columbus Day — 2nd Mon Oct
    add(observed(y, 10, 11));      // Veterans Day — Nov 11
    add(nthWeekday(y, 10, THU, 4));// Thanksgiving — 4th Thu Nov
    add(observed(y, 11, 25));      // Christmas Day — Dec 25
    add(observed(y + 1, 0, 1));    // next New Year's, in case it's observed Dec 31 this year
    holidayCache[y] = set;
    return set;
  }

  // Date whose LOCAL fields (getDay/getHours/getDate…) represent Chicago wall-clock.
  // toLocaleString with an explicit timeZone accounts for CST/CDT (DST) automatically.
  function nowCST() {
    return new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Chicago' }));
  }

  function isHoliday(d) {
    return Object.prototype.hasOwnProperty.call(holidaysForYear(d.getFullYear()), holidayKey(d));
  }

  // A day we actually dispatch on: a weekday that isn't a USPS holiday.
  function isShipDay(d) {
    var day = d.getDay();             // 0 = Sun … 6 = Sat
    if (day === 0 || day === 6) return false; // no weekend dispatch
    if (isHoliday(d)) return false;           // USPS closed
    return true;
  }

  // True only if the order placed right now can still go out today.
  function shipsToday(now) {
    return isShipDay(now) && now.getHours() < CUTOFF;
  }

  // The date the order will actually ship: today if before cutoff on a ship day,
  // otherwise the next open weekday (skipping weekends + holidays).
  function nextShipDate(now) {
    var d = new Date(now.getTime());
    if (shipsToday(now)) return d;
    var guard = 0; // safety bound in case HOLIDAYS ever blanketed a stretch
    do {
      d.setDate(d.getDate() + 1);
      d.setHours(0, 0, 0, 0);
      guard++;
    } while (!isShipDay(d) && guard < 14);
    return d;
  }

  function startOfDay(d) {
    var s = new Date(d.getTime());
    s.setHours(0, 0, 0, 0);
    return s;
  }

  // Localized strings injected by snippets/fc-shipping-i18n.liquid, with the
  // English fallbacks below so the script still works if that object is absent.
  function i18n() {
    return (typeof window !== 'undefined' && window.FC_SHIPPING_I18N) || {};
  }

  function getText() {
    var dict = i18n();
    var now  = nowCST();
    var ship = nextShipDate(now);
    var diff = Math.round((startOfDay(ship) - startOfDay(now)) / 86400000);
    if (diff <= 0) return dict.today || 'SHIPS TODAY';
    if (diff === 1) return dict.tomorrow || 'SHIPS TOMORROW';
    var weekdays = dict.weekdays || WEEKDAYS;
    var template = dict.shipsOn || 'SHIPS {day}';
    return template.replace('{day}', weekdays[ship.getDay()]);
  }

  function inject(selector) {
    var el = document.querySelector(selector);
    if (el) el.textContent = getText();
  }

  window.FCShipping = { getText: getText, inject: inject };

  // Auto-inject into the hero eyebrow span
  function run() { inject('#fc-eyebrow-ship-day'); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
